import { Inject, Injectable, APP_INITIALIZER, Provider } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { map, catchError, tap } from 'rxjs/operators';
import { Observable, BehaviorSubject, forkJoin, Observer, of } from 'rxjs';
import { keyBy } from '../utils/helpers';
import { MeInfo, MemberInfo, TeamInfo, AppGlobalInfo } from '../entities';
import * as utilKits from '@worktile/util-kits';
import { AppRootApiService } from './app-root-api.service';
import { TranslateService } from '@ngx-translate/core';

const globalLoadingSelector = '#global-loading';

const pathAliasModuleNameMap = {
    tasks: 'task',
    messages: 'message'
};

const appGlobal: AppGlobalInfo = ((window as any).appGlobal = {} as AppGlobalInfo);

@Injectable()
export class AppRootContext {
    public loadingDone = false;

    // æ¶æ¯æ°
    public show = 0;

    // å¿ææ°
    public isSnooze = false;

    public globalInfo = appGlobal;

    // å½åç»å½ç¨æ·
    public me$: BehaviorSubject<MeInfo>;

    public get me() {
        return this.me$.value;
    }

    public team: TeamInfo;

    public followIds: string[];

    public followings: MemberInfo[];

    public preferences: any;

    private _firstLoad = true;

    constructor(private rootApiService: AppRootApiService, @Inject(DOCUMENT) private document: any) {}

    private combineInitialData(results) {
        const team = results[0] as TeamInfo;
        if (!team) {
            return (location.href = appGlobal.config.baseUrl);
        }
        const uidMemberMap = keyBy(team.members, 'uid');
        // const currentMember = uidMemberMap[appGlobal.me.uid];
        if (!team.location) {
            team.location = {};
        }
        if (this.team) {
            Object.assign(this.team, team);
        } else {
            this.team = team;
        }

        // followings commands additionalOperates preferences
        this.followIds = results[1];
        this.followings = this.followIds
            .filter(uid => {
                return uidMemberMap[uid];
            })
            .map(uid => {
                return uidMemberMap[uid] as MemberInfo;
            });
        this.preferences = results[2];

        if (!this._firstLoad) {
            // å¹¿æ­äºä»¶
        }
    }

    private isAppRootRoute(rootRouteName: string) {
        if (rootRouteName === 'messages') {
            return true;
        }
        const appName = pathAliasModuleNameMap[rootRouteName] ? pathAliasModuleNameMap[rootRouteName] : rootRouteName;
        return this.team.apps.some(app => {
            return app.name === appName;
        });
    }

    private getCurrentLoginUser() {
        return this.rootApiService.getCurrentLoginUser().pipe(
            map((data: any) => {
                appGlobal.me = data.me;
                appGlobal.config = data.config;
                // ç§æé¨ç½² baseUrl ä¿®æ¹æ location.origin
                if (appGlobal.config.isIndependent) {
                    appGlobal.config.baseUrl = location.origin;
                }
                this.me$ = new BehaviorSubject<MeInfo>(appGlobal.me);
                return appGlobal.me;
            }),
            catchError((response: any) => {
                let showText = '';
                if (response.status === 404) {
                    showText = `API HOST 404, responseText: ${response.responseText}`;
                } else {
                    showText = JSON.stringify(response);
                }
                const $loadingTips = document.querySelector('.loading-tips');
                if ($loadingTips) {
                    $loadingTips.innerHTML = showText;
                } else {
                    alert(showText);
                }
                return of(response);
            })
        );
    }

    private loadI18nData(me: MeInfo) {
        let locale = '';
        if (me && me.preferences && me.preferences.locale) {
            locale = me.preferences.locale;
        } else {
            locale = utilKits.cookie('lang');
            if (!locale) {
                locale = utilKits.browser.getLanguage();
            }
        }
        if (['en-us', 'zh-cn', 'zh-tw', 'ja-jp', 'de'].indexOf(locale) < 0) {
            locale = 'zh-cn';
        }
        appGlobal.locale = locale;
        return this.rootApiService.getI18nData(locale).pipe(
            tap(result => {
                appGlobal.i18nData = result;
            })
        );
    }

    public routePathIsAvailable(path: string) {
        const rules = path.split('/');
        const modulePath = rules[0] === '' ? rules[1] : rules[0];
        return this.isAppRootRoute(modulePath);
    }

    public routeIsNeedPreload(rootRouteName: string) {
        const isApp = this.isAppRootRoute(rootRouteName);
        if (isApp) {
            return !location.pathname.includes(`/${rootRouteName}`);
        } else {
            return false;
        }
    }

    public loadInitialData(): Observable<void> {
        return Observable.create((observer: Observer<any>) => {
            this.getCurrentLoginUser().subscribe(me => {
                if (me) {
                    this.loadI18nData(me).subscribe(() => {
                        return forkJoin([
                            this.rootApiService.getTeam(),
                            this.rootApiService.getFollows(),
                            this.rootApiService.getModulePreference()
                        ])
                            .pipe(
                                map(results => {
                                    this.combineInitialData(results);
                                })
                            )
                            .subscribe(() => {
                                observer.next(null);
                                observer.complete();
                            });
                    });
                } else {
                    if (location.pathname !== '/signin') {
                        location.href = '/signin?returnUrl=' + location.pathname + location.search;
                    }
                    observer.complete();
                }
            });
        });
    }

    public setGlobalLoadingDone() {
        this.loadingDone = true;
        const globalLoadingDOM = this.document.querySelector(globalLoadingSelector);
        if (globalLoadingDOM && globalLoadingDOM.parentNode) {
            globalLoadingDOM.parentNode.removeChild(globalLoadingDOM);
        }
    }

    public updateMe(settings: any) {
        const me = this.me$.getValue();
        Object.assign(me, ...settings);
        this.me$.next(me);
    }

    public updateMePreferences(settings: any) {
        const me = this.me$.getValue();
        Object.assign(me.preferences, ...settings);
        this.me$.next(me);
    }

    public setMessageShow(show: number) {
        this.show = show;
    }
}

export function loadInitialDataFactory(appRootContext: AppRootContext, translate: TranslateService) {
    return () => {
        return new Promise((resolve, reject) => {
            appRootContext.loadInitialData().subscribe(
                () => {
                    translate.use(appRootContext.globalInfo.locale);
                    translate.setTranslation(appRootContext.globalInfo.locale, appRootContext.globalInfo.i18nData);
                    resolve();
                },
                (error) => {
                    reject(error);
                }
            );
        });
    };
}


export const APP_INITIALIZER_PROVIDER: Provider = {
    provide: APP_INITIALIZER,
    useFactory: loadInitialDataFactory,
    deps: [AppRootContext, TranslateService],
    multi: true
};
